import { useEffect, useState } from "react";
import { Lock, Pencil, Check, X, AlertTriangle, Trash2, Loader2, Briefcase, Sparkles } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/shared/components/ui/dialog";
import { Progress } from "@/shared/components/ui/progress";
import { Switch } from "@/shared/components/ui/switch";
import { Textarea } from "@/shared/components/ui/textarea";
import { useToast } from "@/shared/components/ui/use-toast";
import { useAuth } from "@/features/Auth/state/AuthContext";
import { ProfileService } from "../services/profileService";
import { useTranslation } from "react-i18next";

import { logger } from "@/infrastructure/lib/logger";
import { usePasswordResetRequest } from "@/features/Auth/hooks/usePasswordReset";
import { UserService } from "@/features/Users/services/userService";
import { resolveApiErrorMessage } from "@/shared/utils/apiErrorMessage";
import { useNavigate } from "react-router-dom";
import { analyticsService } from "@/shared/services/analytics";
import { TourService } from "@/features/Tour/services/tourService";

/**
 * Qual parte do perfil renderizar (AB#85634, AC23).
 *
 * A tela virou cabecalho + abas, e cada uma mostra um pedaco deste componente.
 * Quebra-lo em arquivos exigiria repartir o estado — o formulario, o
 * carregamento e os dialogos de exclusao sao compartilhados —, e isso seria uma
 * reescrita para resolver um problema de layout.
 *
 *  - `header`      — quem a pessoa e: nome, e-mail e cargo, editaveis no lugar;
 *  - `preferences` — idioma e as instrucoes de IA;
 *  - `security`    — senha e exclusao da conta.
 *
 * Nao ha mais uma aba de dados pessoais: com o nome e o cargo editaveis no
 * cabecalho, ela repetiria na lista o que ja esta em cima.
 */
export type ProfileSection = "header" | "preferences" | "security";

export const ProfileInfo = ({ section = "header" }: { section?: ProfileSection } = {}) => {
  const { user, refreshSession, signOut } = useAuth();
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [displayName, setDisplayName] = useState(user?.user_metadata?.full_name || '');

  // Position (function/role title) editing
  const [position, setPosition] = useState<string>('');
  const [positionDraft, setPositionDraft] = useState<string>('');
  const [isPositionEditing, setIsPositionEditing] = useState(false);
  const [isPositionSaving, setIsPositionSaving] = useState(false);

  // AI preferences (markdown instructions + destructive-action confirmation)
  const [aiInstructions, setAiInstructions] = useState<string>('');
  const [aiInstructionsDraft, setAiInstructionsDraft] = useState<string>('');
  const [aiConfirmDestructive, setAiConfirmDestructive] = useState<boolean>(false);
  const [isAiSaving, setIsAiSaving] = useState(false);
  const [isConfirmSwitchSaving, setIsConfirmSwitchSaving] = useState(false);

  // Load full profile (position + ai_* fields) — useAuth().user has only Supabase auth metadata.
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    ProfileService.getById(user.id)
      .then((p) => {
        if (cancelled || !p) return;
        const pos = p.position ?? '';
        setPosition(pos);
        setPositionDraft(pos);
        const instr = p.ai_instructions ?? '';
        setAiInstructions(instr);
        setAiInstructionsDraft(instr);
        setAiConfirmDestructive(p.ai_confirm_destructive_actions === true);
      })
      .catch((err) => {
        logger.warn('[ProfileInfo] Failed to load profile detail', err);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);
  
  // Estados para exclusão de conta
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [confirmValue, setConfirmValue] = useState(''); // Pode ser senha ou palavra "DELETE"
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState(0);
  
  // Determinar se usuário requer senha (Email/Password) ou confirmWord (OAuth)
  const requiresPassword = user?.requiresPasswordForDelete ?? true;

  // Hook para solicitar recuperação de senha
  const { mutate: requestPasswordReset, isPending: isPasswordResetLoading } = usePasswordResetRequest();

  const handleUpdateDisplayName = async () => {
    try {
      setIsLoading(true);
      
      // Verificar se user.id existe
      if (!user?.id) {
        throw new Error('ID do usuário não encontrado');
      }
      
      // Update profiles table — gateado v2 (`PUT /users/:id`) via ProfileService.
      await ProfileService.update(user.id, { full_name: displayName });

      // Refresh the session to get updated user metadata
      await refreshSession();
      
      setIsEditing(false);
      toast({
        title: t("profile.displayName.updateSuccess"),
        description: t("profile.displayName.updateSuccessDescription"),
      });

      // Track profile update
      analyticsService.trackEvent('profile_updated', {
        fields_changed: ['display_name']
      });
    } catch (error) {
      logger.error("Error:", error);
      toast({
        variant: "destructive",
        title: t("profile.displayName.updateError"),
        description: t("profile.displayName.updateErrorDescription"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSavePosition = async () => {
    if (!user?.id) return;
    const value = positionDraft.trim();
    if (value === position) {
      setIsPositionEditing(false);
      return;
    }
    try {
      setIsPositionSaving(true);
      await ProfileService.update(user.id, { position: value });
      setPosition(value);
      setIsPositionEditing(false);
      toast({
        title: t("profile.position.updateSuccess"),
        description: t("profile.position.updateSuccessDescription"),
      });
      analyticsService.trackEvent('profile_updated', { fields_changed: ['position'] });
    } catch (error) {
      logger.error('[ProfileInfo] Failed to update position:', error);
      toast({
        variant: "destructive",
        title: t("profile.position.updateError"),
        description: t("profile.position.updateErrorDescription"),
      });
    } finally {
      setIsPositionSaving(false);
    }
  };

  const handleSaveAiInstructions = async () => {
    if (!user?.id) return;
    const value = aiInstructionsDraft;
    if (value === aiInstructions) return;
    try {
      setIsAiSaving(true);
      await ProfileService.update(user.id, { ai_instructions: value });
      setAiInstructions(value);
      toast({
        title: t("profile.ai.updateSuccess"),
        description: t("profile.ai.updateSuccessDescription"),
      });
      analyticsService.trackEvent('profile_updated', { fields_changed: ['ai_instructions'] });
    } catch (error) {
      logger.error('[ProfileInfo] Failed to update ai_instructions:', error);
      toast({
        variant: "destructive",
        title: t("profile.ai.updateError"),
        description: t("profile.ai.updateErrorDescription"),
      });
    } finally {
      setIsAiSaving(false);
    }
  };

  const handleToggleConfirmDestructive = async (next: boolean) => {
    if (!user?.id) return;
    const previous = aiConfirmDestructive;
    setAiConfirmDestructive(next);
    try {
      setIsConfirmSwitchSaving(true);
      await ProfileService.update(user.id, { ai_confirm_destructive_actions: next });
      analyticsService.trackEvent('profile_updated', { fields_changed: ['ai_confirm_destructive_actions'] });
    } catch (error) {
      logger.error('[ProfileInfo] Failed to update ai_confirm_destructive_actions:', error);
      setAiConfirmDestructive(previous);
      toast({
        variant: "destructive",
        title: t("profile.ai.updateError"),
        description: t("profile.ai.updateErrorDescription"),
      });
    } finally {
      setIsConfirmSwitchSaving(false);
    }
  };

  const handleResetPassword = async () => {
    // Garantir que o idioma nunca seja undefined
    const locale = i18n.language ? (i18n.language === "pt" ? "pt-BR" : "en-US") : "pt-BR";
    
    // Verificar se temos o email do usuário
    if (!user?.email) {
      toast({
        variant: "destructive",
        title: t("common.error"),
        description: t("profile.emailNotFound"),
      });
      return;
    }
    
    requestPasswordReset(
      {
        email: user.email,
        locale: locale
      },
      {
        onSuccess: (data) => {
          if (data.success) {
            toast({
              title: t("common.success"),
              description: t("profile.passwordResetEmailSent"),
            });
          } else {
            toast({
              variant: "destructive",
              title: t("common.error"),
              description: data.message || t("profile.passwordResetError"),
            });
          }
        },
        // `unknown`, e não `any`: o erro só é consumido por
        // `resolveApiErrorMessage`, que já recebe `unknown` e faz o estreitamento.
        onError: (error: unknown) => {
          logger.error("Error requesting password reset:", error);
          toast({
            variant: "destructive",
            title: t("common.error"),
            description: resolveApiErrorMessage(error, t),
          });
        }
      }
    );
  };

  const handleDeleteAccount = async () => {
    if (!user?.id) {
      toast({
        variant: "destructive",
        title: t("common.error"),
        description: t("profile.deleteAccount.userNotFound"),
      });
      return;
    }

    // Validação diferente para OAuth vs Email/Password
    if (!confirmValue) {
      toast({
        variant: "destructive",
        title: t("common.error"),
        description: requiresPassword 
          ? t("profile.deleteAccount.passwordRequired")
          : t("profile.deleteAccount.confirmWordRequired"),
      });
      return;
    }

    // Para OAuth, validar se digitou "DELETE" corretamente (case-insensitive)
    if (!requiresPassword && confirmValue.toUpperCase() !== 'DELETE') {
      toast({
        variant: "destructive",
        title: t("common.error"),
        description: t("profile.deleteAccount.incorrectConfirmWord"),
      });
      return;
    }

    // Fechar o dialog de confirmação e iniciar o loading
    setIsDeleteDialogOpen(false);
    setIsDeleting(true);
    setDeleteProgress(0);

    // Simular progresso da exclusão
    const progressInterval = setInterval(() => {
      setDeleteProgress(prev => {
        const newProgress = prev + (Math.random() * 15);
        return Math.min(newProgress, 90);
      });
    }, 200);

    try {
      const result = await UserService.deleteSelf(confirmValue, requiresPassword);
      
      // Completar progresso
      clearInterval(progressInterval);
      
      if (result.success) {
        setDeleteProgress(100);
        
        toast({
          title: t("profile.deleteAccount.success"),
          description: t("profile.deleteAccount.successDescription"),
        });
        
        // Aguardar animação antes de fazer logout
        setTimeout(async () => {
          setIsDeleting(false);
          
          // Limpar tour antes do logout
          TourService.clearSessionCache();
          
          // Fazer logout do sistema (limpa sessão, cookies e localStorage)
          await signOut();
          
          // Redirecionar para login
          navigate('/login');
        }, 1000);
      } else {
        // Erro retornado pelo backend
        throw new Error(result.message);
      }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      clearInterval(progressInterval);
      setIsDeleting(false);
      setDeleteProgress(0);
      
      // Verificar se é erro de senha incorreta (por code do backend; fallback por texto cru)
      const rawMessage = (error instanceof Error ? error.message : '') || '';
      const isPasswordError =
        (error as { code?: string })?.code === 'E_INVALID_DELETION_PAYLOAD' ||
        rawMessage.toLowerCase().includes('senha') ||
        rawMessage.toLowerCase().includes('password') ||
        rawMessage.toLowerCase().includes('incorret') ||
        rawMessage.toLowerCase().includes('inválid');

      toast({
        variant: "destructive",
        title: isPasswordError ? t("profile.deleteAccount.incorrectPassword") : t("profile.deleteAccount.error"),
        description: resolveApiErrorMessage(error, t),
      });
      
      // Se for erro de validação, reabrir o dialog
      if (isPasswordError) {
        setIsDeleteDialogOpen(true);
        setConfirmValue('');
      }
    }
  };

  return (
    <div className="space-y-3">
      {/* Cabecalho de identidade (AB#85634, AC23).
          Nome e cargo sao editados AQUI, no lugar onde aparecem. Antes eram uma
          linha que mostrava o nome e, logo abaixo, outra linha que mostrava o
          mesmo nome para edita-lo — o valor repetido duas vezes na mesma tela.

          O lapis so aparece no hover (e sempre com foco de teclado, senao quem
          nao usa mouse nao alcanca o botao). */}
      {section === "header" && (
      <div className="min-w-0 space-y-1">
        {isEditing ? (
          <div className="flex items-center gap-2">
            <Input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="h-9 max-w-xs text-lg font-semibold"
              placeholder={t("profile.displayName.placeholder")}
              autoFocus
            />
            <Button size="icon" variant="ghost" onClick={handleUpdateDisplayName} disabled={isLoading}>
              <Check className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => {
                setIsEditing(false);
                setDisplayName(user?.user_metadata?.full_name || '');
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="group/nome flex items-center gap-1">
            <p className="truncate text-xl font-semibold">
              {user?.user_metadata?.full_name || t("profile.notSet")}
            </p>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 opacity-0 transition-opacity group-hover/nome:opacity-100 focus-visible:opacity-100"
              aria-label={t("profile.displayName.label")}
              onClick={() => setIsEditing(true)}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}

        <p className="truncate text-sm text-muted-foreground">{user?.email}</p>

        {isPositionEditing ? (
          <div className="flex items-center gap-2 pt-1">
            <Input
              value={positionDraft}
              onChange={(e) => setPositionDraft(e.target.value)}
              className="h-8 max-w-xs"
              placeholder={t("profile.position.placeholder")}
              autoFocus
            />
            <Button
              size="icon"
              variant="ghost"
              onClick={handleSavePosition}
              disabled={isPositionSaving}
            >
              {isPositionSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => {
                setIsPositionEditing(false);
                setPositionDraft(position);
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="group/cargo flex items-center gap-1">
            <Briefcase className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <p className="truncate text-sm text-muted-foreground">
              {position || t("profile.notSet")}
            </p>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 opacity-0 transition-opacity group-hover/cargo:opacity-100 focus-visible:opacity-100"
              aria-label={t("profile.position.label")}
              onClick={() => setIsPositionEditing(true)}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>
      )}

      {/* AI preferences — aba propria desde o AB#85634 (AC23).
          A CAIXA fica: ela agrupa dois assuntos que andam juntos — as instrucoes
          pessoais e a confirmacao de acao destrutiva — e os separa do resto do
          perfil. O que saiu foi a SANFONA: ela existia quando isto era um bloco
          opcional no fim de uma pagina longa, e numa aba dedicada so escondia
          atras de um clique o campo que E o conteudo da aba. */}
      {section === "preferences" && (
      <div className="space-y-6 rounded-lg bg-secondary/50 p-6">
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Sparkles className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">{t("profile.ai.sectionTitle")}</p>
              <p className="text-xs text-muted-foreground">{t("profile.ai.sectionDescription")}</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="ai-instructions" className="text-sm font-medium">
              {t("profile.ai.instructionsLabel")}
            </Label>
            {/* Mais alto do que era: instrucao pessoal costuma passar de tres
                linhas, e rolar dentro de um campo curto atrapalha reler o que
                ja se escreveu. */}
            <Textarea
              id="ai-instructions"
              value={aiInstructionsDraft}
              onChange={(e) => setAiInstructionsDraft(e.target.value)}
              placeholder={t("profile.ai.instructionsPlaceholder")}
              rows={12}
              className="font-mono text-sm"
              disabled={isAiSaving}
            />
            <p className="text-xs text-muted-foreground">{t("profile.ai.instructionsHint")}</p>
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setAiInstructionsDraft(aiInstructions)}
                disabled={isAiSaving || aiInstructionsDraft === aiInstructions}
              >
                {t("profile.ai.cancel")}
              </Button>
              <Button
                size="sm"
                onClick={handleSaveAiInstructions}
                disabled={isAiSaving || aiInstructionsDraft === aiInstructions}
              >
                {isAiSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {t("profile.ai.save")}
              </Button>
            </div>
          </div>
        </div>

        <div className="flex items-start justify-between gap-4 border-t border-border/40 pt-4">
          <div className="flex-1">
            <Label htmlFor="ai-confirm-destructive" className="text-sm font-medium">
              {t("profile.ai.confirmDestructiveLabel")}
            </Label>
            <p className="text-xs text-muted-foreground mt-1">
              {t("profile.ai.confirmDestructiveHint")}
            </p>
          </div>
          <Switch
            id="ai-confirm-destructive"
            checked={aiConfirmDestructive}
            onCheckedChange={handleToggleConfirmDestructive}
            disabled={isConfirmSwitchSaving}
          />
        </div>
      </div>
      )}

      {/* Segurança: acesso e ciclo de vida da conta (AB#85634, AC23).
          A senha veio da aba Main porque não é sobre QUEM a pessoa é, e sim
          sobre como ela entra. A exclusão fica por último e mantém o tratamento
          vermelho: trocar senha é rotina, apagar a conta é irreversível, e
          vizinhança sem separação visual convida ao clique errado. */}
      {section === "security" && (
      <>
      <div className="space-y-1 pb-1">
        <p className="text-xs text-muted-foreground">{t("profile.sections.securityHint")}</p>
      </div>

      <div className="flex items-center gap-4 rounded-lg bg-secondary/50 px-5 py-4">
        <Lock className="h-5 w-5 text-muted-foreground" />
        <div className="flex-1">
          <p className="text-sm font-medium">{t("profile.passwordLabel")}</p>
          <p className="text-sm text-muted-foreground">••••••••</p>
        </div>
        <Button
          variant="outline"
          onClick={handleResetPassword}
          disabled={isLoading || isPasswordResetLoading}
        >
          {t("profile.changePassword")}
        </Button>
      </div>

      {section === "security" && (
      <div className="border border-destructive/20 rounded-lg p-6 bg-destructive/5 mt-8">
        <div className="flex items-start gap-3 mb-4">
          <div className="p-2 rounded-full bg-destructive/10">
            <AlertTriangle className="h-5 w-5 text-destructive" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-destructive">{t("profile.deleteAccount.title")}</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {t("profile.deleteAccount.description")}
            </p>
          </div>
        </div>
        
        <div className="border-t border-destructive/20 pt-4">
          <div className="flex justify-between items-center">
            <div>
              <h4 className="font-medium">{t("profile.deleteAccount.buttonTitle")}</h4>
              <p className="text-sm text-muted-foreground mt-1">
                {t("profile.deleteAccount.buttonDescription")}
              </p>
            </div>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setIsDeleteDialogOpen(true)}
              className="shrink-0"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {t("profile.deleteAccount.deleteButton")}
            </Button>
          </div>
        </div>
      </div>
      )}
      </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={(open) => {
        setIsDeleteDialogOpen(open);
        if (!open) {
          setConfirmValue('');
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("profile.deleteAccount.confirmTitle")}</DialogTitle>
            <DialogDescription>
              {t("profile.deleteAccount.confirmDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="confirmValue">
                {requiresPassword 
                  ? t("profile.deleteAccount.confirmLabel")
                  : t("profile.deleteAccount.confirmLabelOAuth")}
              </Label>
              <Input
                id="confirmValue"
                type={requiresPassword ? "password" : "text"}
                value={confirmValue}
                onChange={(e) => setConfirmValue(e.target.value)}
                placeholder={requiresPassword 
                  ? t("profile.deleteAccount.passwordPlaceholder")
                  : t("profile.deleteAccount.confirmWordPlaceholder")}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {requiresPassword 
                  ? t("profile.deleteAccount.confirmHint")
                  : t("profile.deleteAccount.confirmHintOAuth")}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setConfirmValue('');
              }}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={isDeleting || !confirmValue}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("profile.deleteAccount.deleting")}
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t("profile.deleteAccount.confirmButton")}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Progress Dialog */}
      <Dialog open={isDeleting} onOpenChange={setIsDeleting}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">
              {t("profile.deleteAccount.deletingTitle")}
            </DialogTitle>
            <DialogDescription className="text-center">
              {t("profile.deleteAccount.deletingDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="py-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>{t("profile.deleteAccount.deletingStep")}</span>
                  <span className="font-medium">{Math.round(deleteProgress)}%</span>
                </div>
                <Progress value={deleteProgress} className="h-2" />
                <p className="text-xs text-center text-muted-foreground mt-2">
                  {deleteProgress < 100
                    ? t("profile.deleteAccount.deletingMessage")
                    : t("profile.deleteAccount.deletedMessage")}
                </p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
